import { FC } from 'react'

import { Link, useParams } from '@remix-run/react'

import { IconMapPin, IconTrain } from '~/components'
import { NO_IMAGE_URL } from '~/constants'
import {
  officeOtherAccessToText,
  officeStationsToText,
} from '~/features/plan-detail-page/helpers/convert-to-text'
import { twMerge } from '~/lib'
import { Office } from '~/models/types'
import { encodeImageUrl } from '~/utils/encode-image-url'

type OfficeCardProps = {
  office: Pick<
    Office,
    'name' | 'images' | 'stations' | 'otherAccesses' | 'address' | 'id'
  >
}

export const OfficeCard: FC<OfficeCardProps> = ({ office }) => {
  const { prefectureSlug } = useParams<'prefectureSlug'>()
  const stationText = officeStationsToText(office.stations)
  const otherAccessText = officeOtherAccessToText(office.otherAccesses)
  const sortedImages = office.images.sort((a, b) => a.priority - b.priority)
  const firstImage =
    sortedImages.find((image) => image.isEyeCatch) || sortedImages[0]

  return (
    <Link
      className={twMerge(
        'flex gap-x-2 rounded border border-grayBorder p-4 hover:opacity-80 sm:gap-x-4',
      )}
      to={`/prefectures/${prefectureSlug}/offices/${office.id}`}
    >
      <div className="basis-2/5 bg-grayBorder md:basis-1/3">
        <img
          alt={`${office.name}の画像`}
          className="h-full w-full object-cover"
          src={firstImage?.url ? encodeImageUrl(firstImage.url) : NO_IMAGE_URL}
        />
      </div>
      <div className="flex w-3/5 flex-col justify-center md:w-2/3">
        <p className="mb-4 text-sm font-bold leading-[130%] text-link md:text-lg md:leading-[140%]">
          {office.name}
        </p>
        <div>
          <div className="mb-2 flex items-center gap-x-2">
            <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-brand-bg md:h-[30px] md:w-[30px]">
              <IconMapPin
                className="h-[14px] w-[14px] md:h-[18px] md:w-[18px]"
                color="brand"
              />
            </div>
            <p className="truncate text-xs leading-[130%] md:text-body-s">
              {office.address}
            </p>
          </div>
          <div className="flex items-center gap-x-2">
            <div className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-brand-bg md:h-[30px] md:w-[30px]">
              <IconTrain
                className="h-[14px] w-[14px] md:h-[18px] md:w-[18px]"
                color="brand"
              />
            </div>
            <p className="truncate text-xs leading-[130%] md:text-body-s">
              {stationText || otherAccessText
                ? `${stationText}${otherAccessText && `、 ${otherAccessText}`}`
                : '-'}
            </p>
          </div>
        </div>
      </div>
    </Link>
  )
}
